import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import MarcheFormModal, { ETAT_BADGE, getEtat, formatMontant } from './MarcheFormModal';

// ─── Constantes ───────────────────────────────────────────────────────────────
const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const JOURS_COURTS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

// Couleur des marchés sans entité rattachée
const COLOR_SANS_ENTITE = '#7f8c8d';
const SANS_ENTITE = '__none__';

const MAX_EVENTS_CELL = 3; // au-delà : « + n autres »

// ─── Styles (hover, transitions, grille) ─────────────────────────────────────
const CAL_CSS = `
  .cal-anim { animation: calFade .25s ease; }
  @keyframes calFade { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: none; } }

  .cal-seg { display: inline-flex; background: #eef1f4; border-radius: 10px; padding: 3px; }
  .cal-seg button {
    border: none; background: transparent; padding: 0.45rem 1.15rem; border-radius: 8px;
    font-weight: 600; font-size: 0.85rem; color: #5d6d7e; cursor: pointer; transition: all .18s;
  }
  .cal-seg button.active { background: #fff; color: #2c3e50; box-shadow: 0 1px 4px rgba(44,62,80,.18); }

  .cal-navbtn {
    width: 34px; height: 34px; border-radius: 50%; border: 1px solid #e3e7ea; background: #fff;
    cursor: pointer; color: #5d6d7e; font-size: .8rem;
    display: inline-flex; align-items: center; justify-content: center; transition: all .15s;
  }
  .cal-navbtn:hover { background: #f0f7fc; border-color: #3498db; color: #3498db; }
  .cal-today-btn {
    padding: .42rem 1rem; border-radius: 18px; border: 1px solid #e3e7ea; background: #fff;
    font-weight: 600; font-size: .82rem; color: #2c3e50; cursor: pointer; transition: all .15s;
  }
  .cal-today-btn:hover { border-color: #3498db; color: #3498db; background: #f0f7fc; }

  .cal-chip {
    display: inline-flex; align-items: center; gap: .45rem; padding: .35rem .85rem;
    border-radius: 18px; font-size: .82rem; font-weight: 600; cursor: pointer;
    border: 1.5px solid; background: #fff; transition: all .15s; user-select: none;
  }
  .cal-chip:hover { transform: translateY(-1px); box-shadow: 0 2px 6px rgba(44,62,80,.12); }
  .cal-chip .cal-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
  .cal-chip .cal-count { font-size: .7rem; font-weight: 700; opacity: .65; }
  .cal-chip.off { border-color: #e3e7ea !important; color: #aab4bd !important; background: #fafbfc; }
  .cal-chip.off .cal-dot { background: #cfd6dc !important; }

  /* minmax(0,1fr) : empêche les contenus longs d'élargir les colonnes */
  .cal-grid7 { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 6px; }
  .cal-grid5 { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 6px; }
  .cal-dayhead {
    text-align: center; font-size: .68rem; font-weight: 700; letter-spacing: .12em;
    text-transform: uppercase; color: #aab4bd; padding: .35rem 0;
  }

  .cal-cell {
    background: #fafbfc; border: 1px solid #eef1f4; border-radius: 10px;
    min-height: 118px; min-width: 0; padding: 7px; transition: background .15s, box-shadow .15s;
    display: flex; flex-direction: column; overflow: hidden;
  }
  .cal-cell:hover { background: #fff; box-shadow: 0 3px 10px rgba(44,62,80,.09); }
  .cal-cell.we { background: #f4f6f8; }
  .cal-cell.out { background: transparent; border-color: #f4f6f8; }
  .cal-cell.today { background: #fff; border: 2px solid #3498db; padding: 6px; box-shadow: 0 2px 10px rgba(52,152,219,.18); }
  .cal-daynum {
    width: 24px; height: 24px; display: inline-flex; align-items: center; justify-content: center;
    border-radius: 50%; font-size: .78rem; font-weight: 600; color: #5d6d7e; margin-bottom: 4px;
  }
  .cal-daynum.out { color: #ccd3d9; }
  .cal-daynum.today { background: #3498db; color: #fff; box-shadow: 0 2px 6px rgba(52,152,219,.4); }

  .cal-event {
    border-radius: 7px; padding: 3px 7px 4px 10px; font-size: .72rem; cursor: pointer;
    position: relative; overflow: hidden; margin-bottom: 3px;
    transition: box-shadow .12s;
  }
  .cal-event::before {
    content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px;
    background: var(--ec); border-radius: 4px 0 0 4px;
  }
  .cal-event:hover { box-shadow: 0 2px 8px rgba(44,62,80,.18); }
  .cal-event .cal-ev-line2 { color: #5d6d7e; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .cal-more {
    border: none; background: transparent; color: #3498db; font-size: .7rem; font-weight: 700;
    cursor: pointer; padding: 1px 4px; border-radius: 5px; text-align: left;
  }
  .cal-more:hover { background: #eaf4fd; }

  .cal-popup-backdrop { position: fixed; inset: 0; z-index: 900; background: rgba(44,62,80,.08); }
  .cal-popup {
    position: fixed; z-index: 901; background: #fff; border-radius: 12px;
    box-shadow: 0 12px 40px rgba(44,62,80,.3); border: 1px solid #eef1f4;
    padding: .7rem .8rem .6rem; width: 290px; max-height: 62vh; overflow-y: auto;
    animation: calPop .16s ease;
  }
  @keyframes calPop { from { opacity: 0; transform: scale(.93); } to { opacity: 1; transform: none; } }
  .cal-popup-close {
    border: none; background: transparent; font-size: 1.15rem; line-height: 1;
    color: #7f8c8d; cursor: pointer; padding: 2px 6px; border-radius: 6px;
  }
  .cal-popup-close:hover { background: #eef1f4; color: #2c3e50; }

  .cal-wday {
    background: #fafbfc; border: 1px solid #eef1f4; border-radius: 12px;
    padding: .6rem; min-height: 320px; min-width: 0; overflow: hidden; transition: box-shadow .15s;
  }
  .cal-wday.we { background: #f4f6f8; }
  .cal-wday.today { background: #fff; border: 2px solid #3498db; box-shadow: 0 3px 14px rgba(52,152,219,.16); }

  .cal-wcard {
    background: #fff; border: 1px solid #eef1f4; border-top: 3px solid var(--ec);
    border-radius: 10px; padding: .55rem .65rem; margin-bottom: .55rem; cursor: pointer;
    box-shadow: 0 1px 3px rgba(44,62,80,.07); transition: transform .15s, box-shadow .15s;
  }
  .cal-wcard:hover { transform: translateY(-2px); box-shadow: 0 8px 18px rgba(44,62,80,.16); }

  .cal-hour-badge {
    background: #eef1f4; color: #5d6d7e; border-radius: 6px; padding: .1rem .4rem;
    font-size: .7rem; font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap;
  }

  /* Éléments réservés à l'impression (masqués à l'écran) */
  .cal-print-header { display: none; }
  .cal-ev-print { display: none; }
  .cal-event-extra { display: none; } /* marchés au-delà de MAX_EVENTS_CELL : visibles seulement à l'impression */
  .cal-print-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 4px; }

  /* ── Impression : mois complet en A4 paysage ── */
  @media print {
    @page { size: A4 landscape; margin: 9mm; }
    body { background: #fff !important; }
    .navbar { display: none !important; }
    .main-content { padding: 0 !important; margin: 0 !important; max-width: none !important; }
    .card { box-shadow: none !important; border: none !important; border-radius: 0 !important; }
    .card-header, .cal-filtres, .cal-more, .cal-popup, .cal-popup-backdrop { display: none !important; }
    .cal-body { padding: 0 !important; }

    .cal-print-header {
      display: flex !important; justify-content: space-between; align-items: flex-end;
      gap: 6mm; padding: 0 0 3mm; margin-bottom: 3mm; border-bottom: 2px solid #2c3e50;
    }

    /* Conserver les couleurs d'entité sur papier */
    .cal-event, .cal-event::before, .cal-daynum, .cal-print-dot, .cal-cell {
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }

    .cal-grid7, .cal-grid5 { gap: 4px; }
    .cal-cell { min-height: 22mm; break-inside: avoid; box-shadow: none !important; }
    .cal-cell.today { border-width: 1px; box-shadow: none !important; }
    .cal-event { break-inside: avoid; box-shadow: none !important; }
    .cal-event-extra { display: block !important; }
    .cal-ev-print { display: block !important; }
    .cal-event .cal-ev-line2 {
      white-space: normal; display: -webkit-box; -webkit-line-clamp: 3;
      -webkit-box-orient: vertical; overflow: hidden;
    }
  }
`;

// ─── Helpers dates (en heure locale pour éviter les décalages de fuseau) ─────
function dateKey(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function startOfWeek(d) {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (r.getDay() + 6) % 7; // lundi = 0
  r.setDate(r.getDate() - day);
  return r;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function isToday(d) {
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

// ─── Composant principal ──────────────────────────────────────────────────────
function Calendrier({ user }) {
  const [marches, setMarches] = useState([]);
  const [clients, setClients] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('mois'); // 'semaine' | 'mois'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selected, setSelected] = useState(null); // Set de codifications d'entités affichées
  const [editMarche, setEditMarche] = useState(null);
  const [dayPopup, setDayPopup] = useState(null); // { day, left, top } : tous les marchés d'un jour en surimpression
  const [hideWeekend, setHideWeekend] = useState(() => localStorage.getItem('cal-hide-weekend') === '1');

  useEffect(() => { localStorage.setItem('cal-hide-weekend', hideWeekend ? '1' : '0'); }, [hideWeekend]);

  const canDelete = user && user.role === 'Admin';

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [rm, rc, re] = await Promise.all([
          axios.get('/api/marches'),
          axios.get('/api/clients'),
          axios.get('/api/entreprises'),
        ]);
        setMarches(rm.data);
        setClients(rc.data);
        setEntreprises(re.data);
        // Par défaut : toutes les entités paramétrées comme affichables
        const affichables = re.data.filter(e => e.afficher_calendrier !== false);
        setSelected(new Set([...affichables.map(e => e.codification), SANS_ENTITE]));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  const fetchMarches = async () => {
    try {
      const r = await axios.get('/api/marches');
      setMarches(r.data);
    } catch (e) { console.error(e); }
  };

  // Couleur d'une entité (paramétrée dans Administration)
  const colorMap = useMemo(() => {
    const map = {};
    entreprises.forEach(e => { map[e.codification] = e.couleur_calendrier || COLOR_SANS_ENTITE; });
    return map;
  }, [entreprises]);

  const colorOf = (m) => m.entreprise_codification ? (colorMap[m.entreprise_codification] || COLOR_SANS_ENTITE) : COLOR_SANS_ENTITE;

  // Entités paramétrées comme affichables dans le calendrier
  const displayableEntreprises = useMemo(
    () => entreprises.filter(e => e.afficher_calendrier !== false),
    [entreprises]
  );
  const displayableSet = useMemo(
    () => new Set(displayableEntreprises.map(e => e.codification)),
    [displayableEntreprises]
  );

  // Y a-t-il des marchés sans entité ?
  const hasSansEntite = useMemo(() => marches.some(m => !m.entreprise_codification), [marches]);

  // Nombre de marchés avec DLRO par entité (affiché sur les pastilles du filtre)
  const countByEntite = useMemo(() => {
    const map = {};
    marches.forEach(m => {
      if (!m.dlro) return;
      const k = m.entreprise_codification || SANS_ENTITE;
      map[k] = (map[k] || 0) + 1;
    });
    return map;
  }, [marches]);

  // Marchés filtrés par entités sélectionnées (DLRO obligatoire pour apparaître)
  const eventsByDay = useMemo(() => {
    const map = {};
    if (!selected) return map;
    marches.forEach(m => {
      if (!m.dlro) return;
      const ent = m.entreprise_codification;
      // Entité explicitement masquée du calendrier : jamais affichée
      if (ent && !displayableSet.has(ent)) return;
      if (!selected.has(ent || SANS_ENTITE)) return;
      const key = dateKey(new Date(m.dlro));
      if (!map[key]) map[key] = [];
      map[key].push(m);
    });
    // Tri par heure de dépôt dans chaque journée
    Object.values(map).forEach(list => list.sort((a, b) => (a.heure_depot || '99') < (b.heure_depot || '99') ? -1 : 1));
    return map;
  }, [marches, selected, displayableSet]);

  // Jours affichés selon la vue, pour le compteur de période
  const periodDays = useMemo(() => {
    let days;
    if (view === 'semaine') {
      const start = startOfWeek(currentDate);
      days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    } else {
      const y = currentDate.getFullYear();
      const mo = currentDate.getMonth();
      const lastDay = new Date(y, mo + 1, 0);
      days = [];
      for (let d = new Date(y, mo, 1); d <= lastDay; d = addDays(d, 1)) days.push(d);
    }
    // Cohérence avec l'affichage : ne pas compter les week-ends s'ils sont masqués
    if (hideWeekend) days = days.filter(d => (d.getDay() + 6) % 7 < 5);
    return days;
  }, [view, currentDate, hideWeekend]);

  const nbPeriode = useMemo(
    () => periodDays.reduce((sum, d) => sum + (eventsByDay[dateKey(d)]?.length || 0), 0),
    [periodDays, eventsByDay]
  );

  const toggleEntreprise = (codif) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(codif)) next.delete(codif); else next.add(codif);
      return next;
    });
  };

  const allCodifs = [...displayableEntreprises.map(e => e.codification), ...(hasSansEntite ? [SANS_ENTITE] : [])];
  const allSelected = selected && allCodifs.every(c => selected.has(c));

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(allCodifs));
  };

  // ── Navigation ───────────────────────────────────────────────────────────────
  const navigatePeriod = (dir) => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      if (view === 'semaine') d.setDate(d.getDate() + dir * 7);
      else d.setMonth(d.getMonth() + dir);
      return d;
    });
  };

  const periodLabel = () => {
    if (view === 'semaine') {
      const start = startOfWeek(currentDate);
      const end = addDays(start, 6);
      if (start.getMonth() === end.getMonth()) {
        return `${start.getDate()} – ${end.getDate()} ${MOIS[end.getMonth()].toLowerCase()} ${end.getFullYear()}`;
      }
      return `${start.getDate()} ${MOIS[start.getMonth()].toLowerCase()} – ${end.getDate()} ${MOIS[end.getMonth()].toLowerCase()} ${end.getFullYear()}`;
    }
    return `${MOIS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  // Impression : toujours le mois complet, même depuis la vue semaine
  const handlePrint = () => {
    setDayPopup(null);
    if (view !== 'mois') {
      setView('mois');
      setTimeout(() => window.print(), 350);
    } else {
      window.print();
    }
  };

  const tooltipOf = (m) => {
    const etat = getEtat(m);
    const heure = m.heure_depot ? m.heure_depot.slice(0, 5) : '';
    return [
      m.codification,
      m.intitule_marche,
      m.lot,
      m.client_nom ? 'Client : ' + m.client_nom : '',
      m.entreprise_nom ? 'Entité : ' + m.entreprise_nom : '',
      heure ? 'Heure de dépôt : ' + heure : '',
      m.montant ? 'Montant : ' + formatMontant(m.montant) + ' €' : '',
      'État : ' + etat,
    ].filter(Boolean).join('\n');
  };

  // ── Pastille marché (vue mois) — `extra` : au-delà du max affiché à l'écran ──
  const renderEventCompact = (m, extra = false) => {
    const etat = getEtat(m);
    const heure = m.heure_depot ? m.heure_depot.slice(0, 5) : '';
    const color = colorOf(m);
    return (
      <div key={m.reference_interne}
        className={'cal-event' + (extra ? ' cal-event-extra' : '')}
        onClick={(e) => { e.stopPropagation(); setDayPopup(null); setEditMarche(m); }}
        title={tooltipOf(m)}
        style={{ '--ec': color, background: color + '16',
          opacity: ['Perdu', 'Terminé'].includes(etat) ? 0.5 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
          <span style={{ fontWeight: 700, color: '#2c3e50' }}>{m.codification || '#' + m.reference_interne}</span>
          {heure && <span style={{ color: '#7f8c8d', fontSize: '.66rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{heure}</span>}
        </div>
        {/* Bailleur — impression uniquement */}
        {m.client_nom && (
          <div className="cal-ev-print" style={{ fontWeight: 700, fontSize: '.68rem', color: '#2c3e50' }}>
            {m.client_nom}
          </div>
        )}
        <div className="cal-ev-line2">{m.intitule_marche || '—'}</div>
        {/* Lot — impression uniquement */}
        {m.lot && (
          <div className="cal-ev-print" style={{ fontStyle: 'italic', fontSize: '.66rem', color: '#5d6d7e' }}>
            {m.lot}
          </div>
        )}
      </div>
    );
  };

  // ── Carte marché (vue semaine) ───────────────────────────────────────────────
  const renderEventCard = (m) => {
    const etat = getEtat(m);
    const badge = ETAT_BADGE[etat] || ETAT_BADGE['En cours'];
    const heure = m.heure_depot ? m.heure_depot.slice(0, 5) : '';
    return (
      <div key={m.reference_interne}
        className="cal-wcard"
        onClick={(e) => { e.stopPropagation(); setEditMarche(m); }}
        title={tooltipOf(m)}
        style={{ '--ec': colorOf(m), opacity: ['Perdu', 'Terminé'].includes(etat) ? 0.55 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.3rem' }}>
          <strong style={{ fontSize: '.82rem', color: '#2c3e50' }}>{m.codification || '#' + m.reference_interne}</strong>
          {heure && <span className="cal-hour-badge">{heure}</span>}
        </div>
        <div style={{ fontSize: '.78rem', color: '#2c3e50', margin: '.25rem 0',
          overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.35 }}>
          {m.intitule_marche || '—'}
        </div>
        {m.client_nom && (
          <div style={{ fontSize: '.72rem', color: '#7f8c8d', marginBottom: '.3rem',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {m.client_nom}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.3rem' }}>
          <span style={{ fontSize: '.72rem', fontWeight: 600, color: '#5d6d7e', fontVariantNumeric: 'tabular-nums' }}>
            {m.montant ? formatMontant(m.montant) + ' €' : ''}
          </span>
          <span style={{ ...badge, padding: '.1rem .45rem', borderRadius: '10px', fontSize: '.66rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {etat}
          </span>
        </div>
      </div>
    );
  };

  // ── Vue Mois ─────────────────────────────────────────────────────────────────
  const renderMois = () => {
    const y = currentDate.getFullYear();
    const mo = currentDate.getMonth();
    const lastDay = new Date(y, mo + 1, 0);
    const gridClass = hideWeekend ? 'cal-grid5' : 'cal-grid7';
    const jours = hideWeekend ? JOURS_COURTS.slice(0, 5) : JOURS_COURTS;
    let cells = [];
    let d = startOfWeek(new Date(y, mo, 1));
    while (d <= lastDay) {
      for (let i = 0; i < 7; i++) { cells.push(d); d = addDays(d, 1); }
    }
    // Masquer samedi/dimanche si demandé (lundi=0 dans notre repère)
    if (hideWeekend) cells = cells.filter(day => (day.getDay() + 6) % 7 < 5);
    return (
      <div className="cal-anim" key={'mois-' + y + '-' + mo}>
        <div className={gridClass} style={{ marginBottom: '6px' }}>
          {jours.map(j => <div key={j} className="cal-dayhead">{j}</div>)}
        </div>
        <div className={gridClass}>
          {cells.map((day) => {
            const inMonth = day.getMonth() === mo;
            const events = eventsByDay[dateKey(day)] || [];
            const today = isToday(day);
            const we = (day.getDay() + 6) % 7 >= 5;
            const hidden = Math.max(0, events.length - MAX_EVENTS_CELL);
            const cls = 'cal-cell' + (today ? ' today' : '') + (!inMonth ? ' out' : '') + (we && inMonth && !today ? ' we' : '');
            return (
              <div key={dateKey(day)} className={cls}>
                <span className={'cal-daynum' + (today ? ' today' : '') + (!inMonth ? ' out' : '')}>
                  {day.getDate()}
                </span>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  {events.map((m, idx) => renderEventCompact(m, idx >= MAX_EVENTS_CELL))}
                  {hidden > 0 && (
                    <button className="cal-more"
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.closest('.cal-cell').getBoundingClientRect();
                        setDayPopup({ day, left: rect.left, top: rect.top });
                      }}
                      title="Voir tous les marchés de ce jour">
                      + {hidden} autre{hidden > 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Vue Semaine ──────────────────────────────────────────────────────────────
  const renderSemaine = () => {
    const start = startOfWeek(currentDate);
    let days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    if (hideWeekend) days = days.slice(0, 5);
    const gridClass = hideWeekend ? 'cal-grid5' : 'cal-grid7';
    return (
      <div className={gridClass + ' cal-anim'} key={'sem-' + dateKey(start)}>
        {days.map((day, i) => {
          const events = eventsByDay[dateKey(day)] || [];
          const today = isToday(day);
          const we = i >= 5;
          const cls = 'cal-wday' + (today ? ' today' : '') + (we && !today ? ' we' : '');
          return (
            <div key={dateKey(day)} className={cls}>
              <div style={{ textAlign: 'center', marginBottom: '.65rem', paddingBottom: '.5rem',
                borderBottom: '1px solid #eef1f4' }}>
                <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.12em',
                  textTransform: 'uppercase', color: today ? '#3498db' : '#aab4bd' }}>
                  {JOURS[i]}
                </div>
                <div style={{ marginTop: '.2rem' }}>
                  <span className={'cal-daynum' + (today ? ' today' : '')}
                    style={{ width: '30px', height: '30px', fontSize: '.95rem', marginBottom: 0 }}>
                    {day.getDate()}
                  </span>
                </div>
              </div>
              {events.length === 0
                ? <div style={{ textAlign: 'center', color: '#dfe4e8', fontSize: '.8rem', paddingTop: '1.2rem' }}>—</div>
                : events.map(m => renderEventCard(m))}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) return <div className="loading">Chargement…</div>;

  return (
    <div className="card">
      <style>{CAL_CSS}</style>

      {/* ── En-tête ── */}
      <div className="card-header" style={{ flexWrap: 'wrap', gap: '.75rem', alignItems: 'center' }}>
        <div>
          <h1 className="card-title" style={{ marginBottom: '.15rem' }}>{periodLabel()}</h1>
          <div style={{ fontSize: '.8rem', color: '#7f8c8d' }}>
            {nbPeriode === 0 ? 'Aucune DLRO sur la période'
              : nbPeriode === 1 ? '1 DLRO sur la période'
              : nbPeriode + ' DLRO sur la période'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
            <button className="cal-navbtn" onClick={() => navigatePeriod(-1)} title="Période précédente">◀</button>
            <button className="cal-today-btn" onClick={() => setCurrentDate(new Date())}>Aujourd'hui</button>
            <button className="cal-navbtn" onClick={() => navigatePeriod(1)} title="Période suivante">▶</button>
          </div>
          <div className="cal-seg">
            <button className={view === 'semaine' ? 'active' : ''} onClick={() => setView('semaine')}>Semaine</button>
            <button className={view === 'mois' ? 'active' : ''} onClick={() => setView('mois')}>Mois</button>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem', cursor: 'pointer',
            fontSize: '.82rem', color: '#5d6d7e', fontWeight: 600, userSelect: 'none', whiteSpace: 'nowrap' }}
            title="Masquer les samedis et dimanches">
            <input type="checkbox" checked={hideWeekend}
              onChange={e => setHideWeekend(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#3498db' }} />
            Masquer le week-end
          </label>
          <button className="cal-today-btn" onClick={handlePrint} title="Imprimer le mois">🖨 Imprimer</button>
        </div>
      </div>

      {/* ── En-tête d'impression (visible uniquement sur papier) ── */}
      <div className="cal-print-header">
        <div>
          <div style={{ fontSize: '15pt', fontWeight: 700, color: '#2c3e50' }}>Calendrier des DLRO</div>
          <div style={{ fontSize: '11pt', color: '#5d6d7e' }}>
            {MOIS[currentDate.getMonth()]} {currentDate.getFullYear()}
            {' — '}{nbPeriode === 0 ? 'aucun marché' : nbPeriode === 1 ? '1 marché' : nbPeriode + ' marchés'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '5mm', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {entreprises.filter(e => selected?.has(e.codification)).map(e => (
            <span key={e.codification} style={{ fontSize: '9pt', color: '#2c3e50', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              <span className="cal-print-dot" style={{ background: colorMap[e.codification] }} />
              {e.nom}
            </span>
          ))}
        </div>
      </div>

      {/* ── Filtre entités ── */}
      <div className="cal-filtres" style={{ padding: '0 1.5rem 1.1rem', display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '.72rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
          color: '#aab4bd', marginRight: '.35rem' }}>Entités</span>
        {displayableEntreprises.map(e => {
          const active = selected?.has(e.codification);
          const color = colorMap[e.codification];
          const count = countByEntite[e.codification] || 0;
          return (
            <button key={e.codification}
              className={'cal-chip' + (active ? '' : ' off')}
              onClick={() => toggleEntreprise(e.codification)}
              style={{ borderColor: color, color: '#2c3e50' }}>
              <span className="cal-dot" style={{ background: color }} />
              {e.nom}
              <span className="cal-count">{count}</span>
            </button>
          );
        })}
        {hasSansEntite && (
          <button
            className={'cal-chip' + (selected?.has(SANS_ENTITE) ? '' : ' off')}
            onClick={() => toggleEntreprise(SANS_ENTITE)}
            style={{ borderColor: COLOR_SANS_ENTITE, color: '#2c3e50' }}>
            <span className="cal-dot" style={{ background: COLOR_SANS_ENTITE }} />
            Sans entité
            <span className="cal-count">{countByEntite[SANS_ENTITE] || 0}</span>
          </button>
        )}
        <button onClick={toggleAll}
          style={{ padding: '.3rem .6rem', borderRadius: '6px', fontSize: '.76rem',
            fontWeight: 600, cursor: 'pointer', border: 'none',
            background: 'transparent', color: '#3498db' }}>
          {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
        </button>
      </div>

      {/* ── Calendrier ── */}
      <div className="cal-body" style={{ padding: '0 1.5rem 1.5rem' }}>
        {view === 'semaine' && renderSemaine()}
        {view === 'mois' && renderMois()}
      </div>

      {/* ── Surimpression : tous les marchés d'un jour ── */}
      {dayPopup && (() => {
        const events = eventsByDay[dateKey(dayPopup.day)] || [];
        const left = Math.max(8, Math.min(dayPopup.left - 12, window.innerWidth - 306));
        const top = Math.max(8, Math.min(dayPopup.top - 12, window.innerHeight - 340));
        return (
          <>
            <div className="cal-popup-backdrop" onClick={() => setDayPopup(null)} />
            <div className="cal-popup" style={{ left, top }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.5rem' }}>
                <div>
                  <div style={{ fontSize: '.66rem', fontWeight: 700, letterSpacing: '.12em',
                    textTransform: 'uppercase', color: '#aab4bd' }}>
                    {JOURS[(dayPopup.day.getDay() + 6) % 7]}
                  </div>
                  <strong style={{ fontSize: '.95rem', color: '#2c3e50' }}>
                    {dayPopup.day.getDate()} {MOIS[dayPopup.day.getMonth()].toLowerCase()} {dayPopup.day.getFullYear()}
                  </strong>
                </div>
                <button className="cal-popup-close" onClick={() => setDayPopup(null)} title="Fermer">×</button>
              </div>
              {events.map(m => renderEventCompact(m))}
            </div>
          </>
        );
      })()}

      {/* ── Modale modification marché ── */}
      {editMarche && (
        <MarcheFormModal
          user={user}
          clients={clients}
          entreprises={entreprises}
          marche={editMarche}
          initialViewOnly={false}
          onClose={() => setEditMarche(null)}
          onSaved={fetchMarches}
          onDelete={canDelete ? async (id) => {
            if (!window.confirm('Supprimer le marché n°' + id + ' ?')) return;
            try {
              await axios.delete('/api/marches/' + id);
              fetchMarches();
            } catch (err) {
              alert(err.response?.data?.error || 'Erreur lors de la suppression.');
            }
          } : null}
        />
      )}
    </div>
  );
}

export default Calendrier;
